import { Box, Paper, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { newsFeedService } from "../../services";
import useTelemetry from "../../utils/useTelemetry";

const useStyles = makeStyles((theme) => ({
    profileDetails: {
        textAlign: "left",
        color: theme.palette.text.secondary,
        borderRadius: '0',
    },
}));

/**
 * Common component for displaying featured articles and news
 * @param {string | object} fullName - Coach's full name (string or object with firstName/lastName)
 * @returns {JSX.Element}
 */
const FeatureArticleNewsContent = ({ fullName = "" }) => {
    const classes = useStyles();
    const [featureArticles, setFeatureArticles] = useState([]);
    const [remainingNews, setRemainingNews] = useState([]);
    const [loading, setLoading] = useState(false);

    // Construct fullName string from object or use string directly
    const getFullNameString = (fullName) => {
        if (typeof fullName === 'object' && fullName !== null) {
            return `${fullName.firstName || ""} ${fullName.lastName || ""}`.trim();
        }
        return fullName || "";
    };

    const fullNameString = getFullNameString(fullName);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        newsFeedService
            .getNewsFeedPosts(5)
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res)
                    ? res
                    : res && Array.isArray(res.data)
                        ? res.data
                        : [];

                // Filter posts where title (lowercase) contains the fullName
                const fullNameLowercase = (fullNameString || "").toLowerCase().trim();
                const feature = [];
                const remaining = [];

                list.forEach((post) => {
                    if (fullNameLowercase && post.title) {
                        const titleLowercase = (post.title || "").toLowerCase();
                        if (titleLowercase.includes(fullNameLowercase)) {
                            feature.push(post);
                        } else {
                            remaining.push(post);
                        }
                    } else {
                        remaining.push(post);
                    }
                });

                setFeatureArticles(feature || []);
                setRemainingNews(remaining || []);
            })
            .catch((err) => {
                if (!cancelled) {
                    setFeatureArticles([]);
                    setRemainingNews([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fullNameString]);

    return (
        <>
            {featureArticles.length > 0 && (
                <Paper
                    className={clsx(classes.profileDetails, "custom-view")}
                    sx={{
                        minHeight: '300px',
                        overflow: 'hidden',
                        mt: '10px',
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? "rgba(255,255,255,0.01)" : theme.palette.primary.main,
                            color: '#ffffff',
                            px: 2,
                            height: 72,
                            display: 'flex',
                            alignItems: 'center',
                            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
                        }}
                    >
                        <Typography variant="subtitle2">Featured Articles</Typography>
                    </Box>
                    <Box sx={{ p: 1 }}>
                        <Stack spacing={1}>
                            {featureArticles.map((post, idx) => (
                                <ArticleItem key={post.id || idx} post={post} />
                            ))}
                        </Stack>
                    </Box>
                </Paper>
            )}

            <Paper
                className={clsx(classes.profileDetails, "custom-view")}
                sx={{
                    minHeight: '300px',
                    overflow: 'hidden',
                    mt: '10px',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? "rgba(255,255,255,0.01)" : theme.palette.primary.main,
                        color: '#ffffff',
                        px: 2,
                        height: 72,
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
                    }}
                >
                    <Typography variant="subtitle2">News</Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                    {remainingNews.length > 0 ? (
                        <Stack spacing={1}>
                            {remainingNews.map((post, idx) => (
                                <ArticleItem key={post.id || idx} post={post} />
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="caption" sx={{ px: 1 }}>
                            No news available.
                        </Typography>
                    )}
                </Box>
            </Paper>
        </>
    );
};

const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const ArticleItem = ({ post }) => {
    const { trackCustomEvents } = useTelemetry();

    const handleClick = () => {
        if (post.externalSourceUrl) {
            trackCustomEvents("News Click", {
                postId: post.id,
                title: post.title,
                externalSourceUrl: post.externalSourceUrl,
            },
            "event"
            );
            window.open(post.externalSourceUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: "flex",
                alignItems: "stretch",
                borderRadius: 1,
                overflow: "hidden",
                cursor: post.externalSourceUrl ? "pointer" : "default",
                height: 160,
                backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : "#fff",
                boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                "&:hover": {
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
                },
            }}
        >
            {post.imageUrl && (
                <Box
                    sx={{
                        flex: "0 0 40%",
                        width: "40%",
                        maxWidth: "40%",
                        alignSelf: "stretch",
                        backgroundImage: `url(${post.imageUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                    }}
                />
            )}
            <Stack
                sx={{
                    flex: 1,
                    minWidth: 0,
                    mt: 1.5,
                    mb: 1.5,
                    ml: 1.5,
                    pr: 2,
                }}
                spacing={0.75}
            >
                <Typography
                    sx={{
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                    dangerouslySetInnerHTML={{ __html: post.title || "" }}
                />
                <Typography variant="caption" color="textSecondary">
                    {formatDate(post.createdAtUTC)}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: 12,
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.4,
                    }}
                    dangerouslySetInnerHTML={{
                        __html: post.shortDescription || post.description || "",
                    }}
                />
            </Stack>
        </Box>
    );
};

export default FeatureArticleNewsContent;
